/**
 * useUserStore — profile data for the authenticated user.
 *
 * Part of the useAuthStore split (architecture review item 4). Owns
 * personally-identifying profile fields (id, email, names, tenant, etc.)
 * plus the visitor-id and route-change telemetry hooks.
 *
 * Session lifecycle (tokens, isAuthenticated, fetch flags) lives in
 * `useSessionStore`. Permissions/entitlements/subscription state live in
 * `useEntitlementsStore`. `useAuthStore` remains as a thin compatibility
 * facade so existing consumers keep working during the staged migration.
 */

import { defineStore } from 'pinia';

export const useUserStore = defineStore('user', {
  state: () => ({
    /* Legacy "user" namespace (from old auth/user actions) */
    userName: null,
    userEmail: null,
    userAvatar: null,
    /* Profile */
    id: null,
    slug: null,
    email: null,
    phonenumber: null,
    company: null,
    firstname: null,
    lastname: null,
    createdOn: null,
    lastLogin: null,
    receiveNewsletter: false,
    requiresTwoFactor: false,
    has_verified_phone_number: false,
    /* Tenant */
    tenant: null,
    tenantId: null,
    tenantType: null,
    /* Telemetry */
    visitorId: null,
  }),

  getters: {
    // Pure getter — reading the visitor id must not write to storage.
    // Persistence is handled by the `setVisitorId` action.
    visitorIdGetter(state) {
      return state.visitorId;
    },
  },

  actions: {
    setPhoneNumber(phonenumber) {
      this.phonenumber = phonenumber;
    },

    setHasVerifiedPhoneNumber(value) {
      this.has_verified_phone_number = value;
    },

    setUserData(payload) {
      this.id = payload.id;
      this.slug = payload.slug;
      if (payload.firstname) this.firstname = payload.firstname;
      if (payload.lastname) this.lastname = payload.lastname;
      if (payload.email) this.email = payload.email;
      if (payload.phonenumber) this.phonenumber = payload.phonenumber;
      if (payload.created_on) this.createdOn = payload.created_on;
      if (payload.last_login) this.lastLogin = payload.last_login;
      if (payload.requiresTwoFactor) this.requiresTwoFactor = payload.requiresTwoFactor;
      if (payload.company) this.company = payload.company;
      if (payload.tenant) this.tenant = payload.tenant;
      if (payload.receiveUpdates) this.receiveNewsletter = payload.receive_newsletter;
      if ('receive_newsletter' in payload) this.receiveNewsletter = payload.receive_newsletter;
      this.tenantId = payload.tenant_id;
      this.tenantType = payload.tenant_type ?? null;
      this.has_verified_phone_number = payload.has_verified_phone_number ?? false;
    },

    clearUserData() {
      this.id = null;
      this.slug = null;
      this.email = null;
      this.firstname = null;
      this.lastname = null;
      this.createdOn = null;
      this.lastLogin = null;
      this.receiveNewsletter = false;
      this.requiresTwoFactor = false;
      this.company = null;
      this.tenantId = null;
      this.tenant = null;
      this.has_verified_phone_number = false;
    },

    setTenant(payload) {
      this.tenant = payload;
    },

    setVisitorId(visitorId) {
      sessionStorage.setItem('visitorId', visitorId);
      localStorage.setItem('visitorId', visitorId);
      this.visitorId = visitorId;
    },

    user(payload) {
      if (payload.name) this.userName = payload.name;
      if (payload.email) this.userEmail = payload.email;
      if (payload.avatar) this.userAvatar = payload.avatar;
    },
  },
});
